import { Grid, Typography } from '@material-ui/core'
import { useLocation } from '@reach/router'
import { navigate } from 'gatsby'
import { TFunction } from 'i18next'
import { Markup } from 'interweave'
import { parse } from 'query-string'
import React, { useEffect, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useAppState } from '../appState'
import chirpyestProImg from '../assets/images/chirpyest-pro.jpg'
import profileActiveIcon from '../assets/images/profile-active.svg'
import axios from '../axios'
import CHRButton from '../components/button'
import MiddleSection from '../components/homeSections/homeMiddleSection'
import CHRInput from '../components/input'
import ItemsPerPageFilter from '../components/ItemsPerPageFilter'
import SignInModal from '../components/signInModal'
import Spinner from '../components/spinner'
import Card from '../components/trendingCard'
import { ENDPOINTS, ROUTES } from '../constants'
import Layout from '../layouts/defaultLayout'
import SEO from '../components/seo'
import { pagedPath } from '../seoConfig'
import { styles } from '../pagesStyles/editPagesStyles'
import CHRArrowButton from './../components/arrowButton'
import CHRSectionContainer from './../components/sectionContainer'
import NotFound from './404'

interface EditPagesProps {
  t: TFunction
  /** Edit page id, injected by the `/edit-pages/:title/:editPageId` matchPath route. */
  editPageId: string
}

// Sort is always trending — there is no user-facing sort control on this page.
const TRENDING_SORT_QUERY = 'sort=trending'

// Page 1 reserves 2 product slots for the promo card (see the products.map below).
const PROMO_CARD_SLOTS = 2

const productsOnPage = (page: number, perPage: number) =>
  page === 1 ? perPage - PROMO_CARD_SLOTS : perPage

const offsetForPage = (page: number, perPage: number) =>
  page === 1 ? 0 : perPage * (page - 1) - PROMO_CARD_SLOTS

/** Total pages needed for `total` products, accounting for the promo card. */
const countPages = (total: number, perPage: number) => {
  const firstPage = productsOnPage(1, perPage)
  if (total <= firstPage) return 1
  return 1 + Math.ceil((total - firstPage) / perPage)
}

const editPages = ({ t, editPageId }: EditPagesProps) => {
  const classes = styles()

  const { search, pathname } = useLocation()
  const { page, size } = parse(search)
  const [appState] = useAppState()

  const [editPage, setEditPage] = useState(null)
  const [pageSize, setPageSize] = useState(Number(size) || 100)
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [products, setProducts] = useState([])
  // Total across the whole edit page (from the API), not just the current slice.
  const [totalProducts, setTotalProducts] = useState(0)
  const [randomPosition, setRandomPosition] = useState(null)
  const [pageNumber, setPageNumber] = useState(Number(page) || 1)
  const [error, setError] = useState('')
  const [isForwardAndEarnLoading, setIsForwardAndEarnLoading] = useState(false)
  const [forwardedFolderId, setForwardedFolderId] = useState(0)
  const forwardedFolderLinkRef = useRef(null)
  const [isOpenSignInModal, setIsOpenSignInModal] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  useEffect(() => {
    appState.userId && setIsLoggedIn(true)
  }, [])

  const updateProducts = (productId: string, shoppingBoardId: number) => {
    setProducts(items => {
      const i = items.findIndex(e => e.product_id === productId)
      return [
        ...items.slice(0, i),
        {
          ...items[i],
          shoppingBoardId,
        },
        ...items.slice(i + 1),
      ]
    })
  }

  const buildEndpoint = (targetPage: number, perPage: number) =>
    `${ENDPOINTS.editPage}/${editPageId}?size=${productsOnPage(
      targetPage,
      perPage
    )}&offset=${offsetForPage(targetPage, perPage)}&${TRENDING_SORT_QUERY}`

  const applyProductsResponse = (data: any) => {
    setProducts(data.products)
    setTotalProducts(
      typeof data.productsCount === 'number'
        ? data.productsCount
        : data.products.length
    )
  }

  useEffect(() => {
    ;(async () => {
      try {
        const initialPage = Number(page) || 1
        const initialSize = Number(size) || 100
        setPageNumber(initialPage)
        setPageSize(initialSize)
        setLoadingProducts(true)

        const res = await axios.get(buildEndpoint(initialPage, initialSize))

        applyProductsResponse(res.data.data)
        setEditPage(res.data.data.editPage)
        let randomNumber = Math.floor(Math.random() * 15 + 8)
        const edgePositions = [11, 15, 19]
        edgePositions.includes(randomNumber) &&
          (randomNumber = randomNumber - 1)
        setRandomPosition(randomNumber)
        setLoadingProducts(false)
      } catch (error) {
        setLoadingProducts(false)
        setError(t('messages.somethingWentWrong'))
      }
    })()
  }, [editPageId])

  const handlePageSizeChange = async (newSize: number) => {
    try {
      // Land on the page that still contains the first product currently shown.
      const firstItemIndex = offsetForPage(pageNumber, pageSize)
      const newPageNumber =
        firstItemIndex === 0
          ? 1
          : Math.floor((firstItemIndex + PROMO_CARD_SLOTS) / newSize) + 1

      setLoadingProducts(true)
      setPageNumber(newPageNumber)
      setPageSize(newSize)

      window.history.pushState('', '', `?page=${newPageNumber}&size=${newSize}`)

      const res = await axios.get(buildEndpoint(newPageNumber, newSize))

      applyProductsResponse(res.data.data)
      setLoadingProducts(false)
    } catch (error) {
      setLoadingProducts(false)
      setError(t('messages.somethingWentWrong'))
    }
  }

  const navigatePage = async (targetPage: number) => {
    try {
      window.scroll({
        top: 200,
        behavior: 'auto',
      })
      setLoadingProducts(true)

      window.history.pushState('', '', `?page=${targetPage}&size=${pageSize}`)

      const res = await axios.get(buildEndpoint(targetPage, pageSize))

      applyProductsResponse(res.data.data)
      setPageNumber(targetPage)
      setLoadingProducts(false)
    } catch (error) {
      setLoadingProducts(false)
      setError(t('messages.somethingWentWrong'))
    }
  }

  const onForwardAndEarnClick = async () => {
    if (!isLoggedIn) {
      setIsOpenSignInModal(true)
      return
    }
    setIsForwardAndEarnLoading(true)
    const endpoint = `${ENDPOINTS.forwardEditPageToShoppingBoard}`.replace(
      ':id',
      editPageId
    )
    const res = await axios.post(endpoint, {})
    if (res.status === 200) {
      setForwardedFolderId(res.data.data.folderId)
    }
    setIsForwardAndEarnLoading(false)
  }

  const copyForwardedFolderLink = (event: any) => {
    if (forwardedFolderLinkRef && forwardedFolderLinkRef?.current !== null) {
      forwardedFolderLinkRef?.current?.select()
      document.execCommand('copy')
      event?.target?.focus()
      window.location.href = forwardedFolderLinkRef?.current?.value
    }
  }

  const fetchPreviousPage = async () => {
    await navigatePage(pageNumber - 1)
  }

  const fetchNextPage = async () => {
    await navigatePage(pageNumber + 1)
  }

  const closeSignInModal = () => {
    setIsOpenSignInModal(false)
  }

  if (error) {
    return <NotFound error={error} />
  }

  if (!editPage) {
    if (loadingProducts) return <Spinner />
    return <NotFound />
  }

  const totalPages = countPages(totalProducts, pageSize)

  return (
    <Layout>
      <SEO
        title={editPage.title}
        // The description is rich text; the meta tag needs it as plain text.
        description={(editPage.description || '')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()}
        image={editPage.logo}
        canonicalPath={pagedPath(pathname, pageNumber)}
      />
      {/* Heading */}
      <div className={classes.headingWrapper}>
        <div className={classes.headingContainer}>
          <div className={classes.headingTextContainer}>
            <img
              src={`${editPage.logo ? editPage.logo : profileActiveIcon}`}
              className={classes.headingLogoImage}
              alt="logo"
            />
            <div className={classes.headingLogoText}>
              <Typography
                component="p"
                className={classes.chirpyestPageTitle}
                variant="subtitle2"
              >
                chirpyest edit
              </Typography>
              <Typography
                component="h1"
                className={classes.chirpyestPageHeader}
                variant="h1"
              >
                {editPage.title}
              </Typography>
              <Typography
                component="p"
                className={classes.description}
                variant="subtitle2"
              >
                <Markup content={editPage.description} />
              </Typography>
            </div>
          </div>
          <div className={classes.actionButtonsContainer}>
            {forwardedFolderId === 0 && (
              <CHRButton
                label={t('shoppingBoard.forwardAndEarn')}
                onClick={onForwardAndEarnClick}
                customStyle={`${classes.btn} ${classes.btnOther}`}
                hoverEffects={true}
                isSubmitting={isForwardAndEarnLoading}
              />
            )}
            {forwardedFolderId !== 0 && (
              <div className={classes.forwardedFolderLink}>
                <CHRInput
                  placeholder=""
                  value={`${window.location.origin}/s/${
                    appState.username
                  }/${editPage.title.replace(
                    /\s+|\/|\\|#/g,
                    '-'
                  )}/${forwardedFolderId}`}
                  onChange={() => {}}
                  type="text"
                  elementRef={forwardedFolderLinkRef}
                />
                {document.queryCommandSupported('copy') && (
                  <CHRButton
                    label={'copy and go'}
                    onClick={copyForwardedFolderLink}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className={classes.productsContainer}>
          <div className={classes.countingFilterContainer}>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Grid item lg={3} md={4} sm={4} xs={6}>
                <Typography
                  component="p"
                  className={classes.itemsCount}
                  variant="body1"
                >
                  {totalProducts}
                  {totalProducts >= 10000 ? '+' : ''} {t('shared.items')}
                </Typography>
              </Grid>
              <Grid
                item
                lg={3}
                md={4}
                sm={5}
                xs={6}
                className={classes.itemsPerPageCell}
              >
                <ItemsPerPageFilter
                  onFilterChange={handlePageSizeChange}
                  selectedFilter={pageSize}
                />
              </Grid>
            </Grid>
          </div>
          <Grid container alignItems="stretch" spacing={2}>
            {loadingProducts ? (
              <Spinner />
            ) : (
              products.map((product, index) => {
                const productItem = (
                  <Grid
                    item
                    lg={3}
                    md={4}
                    sm={6}
                    xs={6}
                    key={product.product_id}
                  >
                    <Card
                      updateProducts={updateProducts}
                      userId={appState.userId}
                      productInfo={product.data}
                      shareable
                    />
                  </Grid>
                )

                // Promo card is inserted before this product, not swapped in for it.
                if (index === randomPosition && pageNumber === 1) {
                  return (
                    <React.Fragment key={`promo-${product.product_id}`}>
                      <Grid item lg={6} md={8} sm={12} xs={12}>
                        <div className={classes.joinCard}>
                          <Typography
                            variant="h4"
                            className={classes.joinCardText}
                          >
                            {editPage.cardText
                              ? editPage.cardText
                              : `we love these products. our ${new Date().getFullYear()}
                            favorites!`}
                          </Typography>
                        </div>
                      </Grid>
                      {productItem}
                    </React.Fragment>
                  )
                }

                return productItem
              })
            )}
          </Grid>
          <div className={classes.paginationContainer}>
            <CHRArrowButton
              isLeftArrow
              label={t('shared.previous')}
              onClick={fetchPreviousPage}
              disabled={loadingProducts || pageNumber <= 1}
            />
            <Typography variant="subtitle2" component="p">
              {pageNumber}/{totalPages}
            </Typography>
            <CHRArrowButton
              label={t('shared.next')}
              onClick={fetchNextPage}
              disabled={loadingProducts || pageNumber >= totalPages}
            />
          </div>
        </div>
      </div>
      {/* Pro section */}
      <CHRSectionContainer>
        <section>
          <MiddleSection
            handleArrowClick={() => navigate(ROUTES.chirpyestPro)}
            image={chirpyestProImg}
            buttonLabel={t('memberHome.learnMore')}
            rightChildren={
              <div>
                <Typography variant="h3">{t('memberHome.proTitle')}</Typography>
                <Typography variant="h2">
                  {t('memberHome.proSubtitle')}
                </Typography>
                <Typography variant="subtitle1" component="p">
                  {t('memberHome.proText')}
                </Typography>
              </div>
            }
          />
        </section>
      </CHRSectionContainer>

      <SignInModal
        handleClose={closeSignInModal}
        openStatus={isOpenSignInModal}
        setIsLoggedIn={setIsLoggedIn}
        allowRedirect={false}
      />
    </Layout>
  )
}
export default withTranslation()(editPages)
